/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState, useEffect } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { LoadingSpinner, Mixins } from '@blend/enterprise-components';
import * as Sentry from '@sentry/browser';
import { get } from 'lodash';

import { getVersionedFees } from 'blb-api/loans';
import { getFeesImport } from 'blb-api/title';
import { BlendBridge } from 'blb-types/blendBridge';
import { FormShape, ErrorType, CurrentRequestType, FeesContextType, CancelRequest } from '../types';
import { ClosingFeesFormContainer } from '../containers/ClosingFeesForm';
import { formatClosingFeesResponse, setIdentifier } from '../utils/closingFeesUtils';
import { Pricing } from 'blb-types/pricing';
import { ClosingFeesSummary } from '../components/ClosingFeesSummary';
import { ErrorAlert } from '../components/ErrorAlert';
import { NewRequestModalContainer } from '../containers/NewRequestModal';
import { DashboardSection } from '../../DashboardSection';
import { CancelRequestModal } from '../components/CancelRequestModal';
import { CHECK_LOS_SYNC_TIME_INTERVAL, DEFAULT_REQUEST, INITIAL_CONTEXT_VALUES } from '../constants';
import { MoreInfoRequestAlertContainer } from './MoreInfoRequestedAlert';
import useLocks from '../../Inbox/useLocks';
import { DrawerContainer } from './Drawer';
import { addOnBeforeUnloadListener, removeOnBeforeUnloadListener } from '../utils/eventListenerUtils';
import { FeesTabWithTimerProps } from './FeesTabWithTimer';
import { TimerProps } from '@blend/shared-react';
import { LOSSyncModal } from '../components/LOSSyncModal/LOSSyncModal';

const pageContentPadding = 40;
const containerLeftWidth = 1000;
const containerRightWidth = 340;
const containerWidth = 2 * pageContentPadding + containerLeftWidth + containerRightWidth;

const styles = StyleSheet.create({
  centeredContainer: {
    ...Mixins.flexCenter,
  },
  closingFeesContainer: {
    display: 'flex',
    minWidth: containerWidth,
  },
  closingFeesContainerContent: {
    display: 'flex',
  },
  closingFeesContainerLeft: {
    marginRight: 24,
    paddingBottom: 24,
    width: containerLeftWidth,
  },
  closingFeesContainerRight: {
    position: 'relative',
    width: containerRightWidth,
  },
  closingFeesContainerRightInner: {
    pointerEvents: 'none', // turn off pointer event so that scrolling works when user is on the right side of the page (gray area)
    position: 'fixed',
    width: containerRightWidth,
  },
});

type FeesTabProps = FeesTabWithTimerProps & TimerProps;

export const FeesContext = React.createContext<FeesContextType>(INITIAL_CONTEXT_VALUES);

export const FeesTab: React.FC<FeesTabProps> = (props: FeesTabProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [version, setVersion] = useState<number>(1);
  const [errorType, setErrorType] = useState<ErrorType>(null);
  const [formInitialValues, setFormInitialValues] = useState<FormShape>({});
  const [formValues, setFormValues] = useState<FormShape>({});
  const [currentRequest, setCurrentRequest] = useState<CurrentRequestType>(DEFAULT_REQUEST);
  const [cancelRequest, setCancelRequest] = useState<CancelRequest | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [lockIsAvailable, grabLock, releaseLock, releaseLockViaFetch] = useLocks(false);
  const [syncTimestamp, setSyncTimestamp] = useState<number>(Date.now());
  const [syncModalOpen, setSyncModalOpen] = useState<boolean>(false);

  const { loan, order, userId, reactSafeSetInterval } = props;
  const contextValue = {
    actionsEnabled: !!lockIsAvailable,
    formInitialValues,
    formValues,
    loan,
    order,
    setCancelRequest,
    setCurrentRequest,
    setErrorType,
    setFormInitialValues,
    setFormValues,
    version,
  };

  const lockParams = {
    loanId: loan.loanId,
    type: BlendBridge.API.LockType.FEE,
    userId: props.userId,
  };

  let syncTimer: null | number = null;

  const releaseLockOnTabClose = () => releaseLockViaFetch(lockParams);
  useEffect(() => {
    addOnBeforeUnloadListener(releaseLockOnTabClose);
    void (async () => {
      try {
        await Promise.all([grabLock(lockParams), fetchVersionedFees().catch(err => setIdentifier('fees', err))]);
        // TODO: enable when feature is ready
        // syncTimer = reactSafeSetInterval(checkLOSSync, CHECK_LOS_SYNC_TIME_INTERVAL);
      } catch (err) {
        const errType: string = get(err, 'identifier', 'default_error');
        switch (errType) {
          case 'lock':
            setErrorType('LockError');
            break;
          case 'fees':
            setErrorType('LoadingError');
            releaseLock(lockParams);
            break;
          default:
            setErrorType('GenericError');
            releaseLock(lockParams);
        }
      } finally {
        setIsLoading(false);
      }
    })();

    return () => {
      removeOnBeforeUnloadListener(releaseLockOnTabClose);
      releaseLock(lockParams);
      clearTimer(syncTimer);
    };
  }, []);

  const fetchVersionedFees = async () => {
    const { data } = await getVersionedFees(loan.loanId, order.tenant);
    initialize(data);
  };

  const initialize = (data: Pricing.VersionedFeesResponse) => {
    const currentVersion = data.fees.length ? data.fees[0].version : 1;
    const formattedResponse = formatClosingFeesResponse(data);
    setVersion(currentVersion);
    setFormInitialValues(formattedResponse);
    setFormValues(formattedResponse);
  };

  const clearTimer = (timerId: null | number) => {
    if (timerId) window.clearInterval(timerId);
  };

  const checkLOSSync = async () => {
    try {
      const {
        data: { syncTime },
      } = await getFeesImport(loan.loanId, order.tenant);
      if (syncTime) {
        const timestamp = new Date(syncTime).getTime();
        if (syncTimestamp < timestamp) {
          clearTimer(syncTimer);
          setSyncTimestamp(timestamp);
          setSyncModalOpen(true);
        }
      }
    } catch (err) {
      Sentry.captureException(err);
      // do nothing otherwise, just wait for the next check
    }
  };

  const handleRefresh = async () => {
    await fetchVersionedFees();
    syncTimer = reactSafeSetInterval(checkLOSSync, CHECK_LOS_SYNC_TIME_INTERVAL);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={css(styles.centeredContainer)}>
          <LoadingSpinner size="lg" theme="secondary" />
        </div>
      );
    }
    return (
      <div className={css(styles.closingFeesContainer)}>
        <NewRequestModalContainer
          currentRequest={currentRequest}
          handleClose={() => setCurrentRequest({ ...currentRequest, displayModal: false })}
          isOpen={currentRequest.displayModal}
        />
        <DrawerContainer
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          tenant={order.tenant}
          topic={BlendBridge.API.Topic.FEE_COLLABORATION}
          userId={userId}
        />
        <CancelRequestModal
          cancelRequest={cancelRequest}
          handleClose={() => setCancelRequest(null)}
          isOpen={!!cancelRequest}
        />
        <LOSSyncModal
          handleClose={() => setSyncModalOpen(false)}
          handleRefresh={handleRefresh}
          isOpen={syncModalOpen}
        />
        <div className={css(styles.closingFeesContainerContent)}>
          <section className={css(styles.closingFeesContainerLeft)}>
            {!!errorType && <ErrorAlert dismiss={() => setErrorType(null)} type={errorType} />}
            <MoreInfoRequestAlertContainer formValues={formValues} loanId={loan.loanId} setDrawerOpen={setDrawerOpen} />
            <ClosingFeesFormContainer
              drawerOpen={drawerOpen}
              grabLock={() => grabLock(lockParams)}
              initialize={initialize}
            />
          </section>
          <section className={css(styles.closingFeesContainerRight)}>
            <div className={css(styles.closingFeesContainerRightInner)} id="it-ContainerRightInner">
              <ClosingFeesSummary formValues={formValues} loan={loan} />
            </div>
          </section>
        </div>
      </div>
    );
  };

  return (
    <FeesContext.Provider value={contextValue}>
      <DashboardSection
        sectionConfigs={[
          {
            content: renderContent(),
            hideTitle: true,
            title: 'Fees',
          },
        ]}
        sectionHeader="Fees"
      />
    </FeesContext.Provider>
  );
};
